/**
 * Root Application Component
 *
 * The main component that serves as the entry point for the BlueRide Companies Dashboard.
 * Manages the layout structure, showing header and sidebar for authenticated users,
 * and a clean auth layout for unauthenticated users.
 *
 * @example
 * Layout Structure (Authenticated):
 * ```
 * <p-toast></p-toast>
 * <app-header></app-header>
 * <div class="main-container">
 *   <app-sidebar></app-sidebar>
 *   <main class="content">
 *     <router-outlet></router-outlet>
 *   </main>
 * </div>
 * ```
 *
 * Layout Structure (Unauthenticated):
 * ```
 * <p-toast></p-toast>
 * <router-outlet></router-outlet>
 * ```
 */

import { Component, OnInit, OnDestroy, HostListener, inject, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AuthService } from './core/services/auth.service';
import { SecurityService } from './core/services/security.service';
import { SecurityLogService } from './core/services/security-log.service';
import { LanguageService } from './core/services/language.service';
import { ChartLocaleService } from './core/services/chart-locale.service';
import { HeaderComponent } from './shared/components/layout/header/header.component';
import { SidebarComponent } from './shared/components/layout/sidebar/sidebar.component';
import { SessionTimeoutComponent } from './shared/components/session-timeout/session-timeout.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    ToastModule,
    HeaderComponent,
    SidebarComponent,
    SessionTimeoutComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  /**
   * Application title
   */
  title = 'BlueRide Companies Dashboard';

  /**
   * Observable that emits authentication state
   * Used to toggle between authenticated and unauthenticated layouts
   */
  isAuthenticated$: Observable<boolean> = inject(AuthService).isAuthenticated$;

  /**
   * Whether to show session timeout warning dialog
   */
  showSessionTimeoutWarning: boolean = false;

  /**
   * Remaining seconds until session expires
   */
  sessionTimeRemaining: number = 0;

  /**
   * Whether sidebar is collapsed
   */
  sidebarCollapsed: boolean = true;

  /**
   * Subscriptions for cleanup
   */
  private subscriptions: Subscription[] = [];

  constructor(
    public authService: AuthService,
    private securityService: SecurityService,
    private securityLog: SecurityLogService,
    private router: Router,
    private _languageService: LanguageService, // Ensures language/RTL is initialized at app startup
    private _chartLocaleService: ChartLocaleService // Ensures Chart.js uses correct locale
  ) {}

  /**
   * Initializes the component
   * Sets up session timeout tracking if authenticated
   */
  @ViewChild('mainContent') mainContent!: ElementRef<HTMLElement>;

  ngOnInit(): void {
    // Scroll content area to top on navigation
    const routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.mainContent?.nativeElement?.scrollTo(0, 0);
      });
    this.subscriptions.push(routerSub);

    // Initialize session timeout tracking if authenticated
    if (this.authService.isAuthenticated()) {
      this.initializeSessionTimeout();
    }
  }

  /**
   * Cleanup on component destruction
   */
  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.securityService.stopSessionTimer();
  }

  /**
   * Track user activity to reset session timer
   */
  @HostListener('document:click')
  @HostListener('document:keypress')
  @HostListener('document:mousemove')
  onUserActivity(): void {
    if (this.authService.isAuthenticated()) {
      this.securityService.resetSessionTimer();
    }
  }

  /**
   * Initializes session timeout tracking
   */
  private initializeSessionTimeout(): void {
    // Start session timer
    this.securityService.startSessionTimer();

    // Listen for session timeout warning
    const warningSub = this.securityService.onSessionWarning.subscribe((seconds) => {
      this.sessionTimeRemaining = seconds;
      this.showSessionTimeoutWarning = true;
    });

    // Listen for session timeout
    const timeoutSub = this.securityService.onSessionTimeout.subscribe(() => {
      this.handleSessionTimeout();
    });

    this.subscriptions.push(warningSub, timeoutSub);
  }

  /**
   * Handles session timeout by logging out user
   */
  private handleSessionTimeout(): void {
    this.showSessionTimeoutWarning = false;
    this.securityLog.logSessionTimeout();

    this.authService.logout().subscribe({
      next: () => {
        this.router.navigate(['/auth/login'], {
          queryParams: { reason: 'session_timeout' }
        });
      },
      error: () => {
        // Even if logout API call fails, navigate to login
        this.router.navigate(['/auth/login'], {
          queryParams: { reason: 'session_timeout' }
        });
      }
    });
  }

  /**
   * Handles user choosing to stay logged in
   */
  onStayLoggedIn(): void {
    this.securityService.resetSessionTimer();
    this.showSessionTimeoutWarning = false;
  }

  /**
   * Handles user choosing to logout
   */
  onLogout(): void {
    this.showSessionTimeoutWarning = false;
    this.authService.logout().subscribe({
      next: () => {
        this.router.navigate(['/auth/login']);
      }
    });
  }

  /**
   * Handles sidebar collapsed state change
   */
  onSidebarCollapsedChange(collapsed: boolean): void {
    this.sidebarCollapsed = collapsed;
  }
}
